import z from 'zod';

const appConfigBaseSchema = z.object({
  API_ROOT: z.url(),
  APOLLO_CLIENT_DEV_TOOLS: z.boolean(),
  CREATIVE_BUILDS_URL: z.url(),
  CREATIVE_MANIFEST_URL: z.url(),
  DD_APP_ID: z.string(),
  DD_CLIENT_TKN: z.string(),
  DD_ENV: z.string(),
  DD_SAMPLE_RATE: z.number().min(0),
  DD_SESSION_REPLAY_SAMPLE_RATE: z.number().min(0),
  DD_SITE: z.string(),
  DD_TRACK_USER_INTERACTIONS: z.boolean(),
  FF_REFETCH_INTERVAL: z.number().min(0),
  INTERCOM_APP_ID: z.string(),
  INTERCOM_MESSENGER_ENABLED: z.boolean(),
  KEYCLOAK_CLIENT_ID: z.string(),
  KEYCLOAK_REALM: z.string(),
  KEYCLOAK_URL: z.string().url(),
  MAPBOX_F11_SOURCE_TILES_URL: z.url(),
  MAPBOX_TOKEN: z.string(),
  MAPBOX_URL: z.string().url(),
  MF_REMOTE_ADCREATOR_URL: z.url(),
  MF_REMOTE_CAMPAIGN_URL: z.url(),
  MF_REMOTE_BOOKING_URL: z.url(),
  MF_REMOTE_CUSTOMER_URL: z.url(),
  MF_REMOTE_MAP_URL: z.url(),
  MF_REMOTE_ORGANIZATION_SETTINGS_URL: z.url(),
  MF_REMOTE_PERSONAL_SETTINGS_URL: z.url(),
  MF_REMOTE_PREVIEW_URL: z.url(),
  MF_REMOTE_REPORTING_URL: z.url(),
  MF_REMOTE_TARGETING_URL: z.url(),
  MF_REMOTE_MEDIAPLANNING_URL: z.url(),
  MF_REMOTE_ASSETS_LIBRARY_URL: z.url(),
  MF_REMOTE_CLIENT_REPORTING_URL: z.url(),
  ORG_ID: z.uuid(),
  PREVIEW_F11_HOST: z.url(),
  PREVIEW_NEUTRAL_HOST: z.url(),
  STAGE_HOST_LIVE: z.url(),
  STAGE_HOST_PREVIEW: z.url(),
  SUPPORTED_LANGUAGES: z.string(),
  THEME_URL: z.url(),
  THEME_DEFAULT: z.string(),
  YOUTUBE_API_KEY: z.string(),
});
const appConfigDerivedSchema = z.object({
  ...appConfigBaseSchema.shape,
  DOWNLOAD_URL: z.url(),
  EXPORT_URL: z.url(),
  FILE_URL: z.url(),
  IMG_URL: z.url(),
});

type BaseAppConfig = z.infer<typeof appConfigBaseSchema>;
type AppConfig = z.infer<typeof appConfigDerivedSchema>;
let appConfig: AppConfig;
// https://factor-eleven.atlassian.net/browse/AR-2464
// TODO: Refactor this module and remove legacy pattern of fetching
// the app configuration, the configuration will always come from
// the window object.
// We can try to remove the loadAppConfig function and load and assert the
// app config the first time that getAppConfig is called.
export function assertInjectedAppConfig(): void {
  // if check for backwards compatibility
  if (globalThis.appConfig) {
    const result = appConfigBaseSchema.safeParse(globalThis.appConfig);
    if (!result.success) {
      throw new Error(
        'Injected app configuration has the wrong shape, app config value:',
        {
          cause: {
            input: globalThis.appConfig,
            error: result.error,
          },
        },
      );
    }
  }
}

function loadAppConfigOverwrites(): Partial<BaseAppConfig> {
  const appConfigOverwrite = localStorage.getItem('DEBUG_APP_CONFIG_OVERWRITE');

  if (appConfigOverwrite) {
    try {
      const partialAppConfig = JSON.parse(appConfigOverwrite);
      if (partialAppConfig && typeof partialAppConfig === 'object') {
        console.warn('DEBUG_APP_CONFIG_OVERWRITE applied', partialAppConfig);
        return partialAppConfig;
      }
    } catch (error) {
      console.info(
        'DEBUG_APP_CONFIG_OVERWRITE ignored, it has an invalid shape',
        {
          appConfigOverwrite,
          error,
        },
      );
    }
  }

  return {};
}

const initAppConfig = (): BaseAppConfig => {
  if (globalThis.appConfig) {
    return {
      ...(globalThis.appConfig as BaseAppConfig),
      ...loadAppConfigOverwrites(),
    };
  }

  const envConfig: BaseAppConfig = {
    API_ROOT: import.meta.env.VITE_API_ROOT,
    APOLLO_CLIENT_DEV_TOOLS:
      import.meta.env.VITE_APOLLO_CLIENT_DEV_TOOLS === 'true',
    CREATIVE_BUILDS_URL: import.meta.env.VITE_CREATIVE_BUILDS_URL,
    CREATIVE_MANIFEST_URL: import.meta.env.VITE_CREATIVE_MANIFEST_URL,
    DD_APP_ID: import.meta.env.VITE_DD_APP_ID,
    DD_CLIENT_TKN: import.meta.env.VITE_DD_CLIENT_TKN,
    DD_ENV: import.meta.env.VITE_DD_ENV,
    DD_SAMPLE_RATE: Number(import.meta.env.VITE_DD_SAMPLE_RATE),
    DD_SESSION_REPLAY_SAMPLE_RATE: Number(
      import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE,
    ),
    DD_SITE: import.meta.env.VITE_DD_SITE,
    DD_TRACK_USER_INTERACTIONS:
      import.meta.env.VITE_DD_TRACK_USER_INTERACTIONS === 'true',
    FF_REFETCH_INTERVAL: Number(import.meta.env.VITE_FF_REFETCH_INTERVAL),
    INTERCOM_APP_ID: import.meta.env.VITE_INTERCOM_APP_ID,
    INTERCOM_MESSENGER_ENABLED:
      import.meta.env.VITE_INTERCOM_MESSENGER_ENABLED === 'true',
    KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
    KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM,
    KEYCLOAK_URL: import.meta.env.VITE_KEYCLOAK_URL,
    MAPBOX_F11_SOURCE_TILES_URL: import.meta.env
      .VITE_MAPBOX_F11_SOURCE_TILES_URL,
    MAPBOX_TOKEN: import.meta.env.VITE_MAPBOX_TOKEN,
    MAPBOX_URL: import.meta.env.VITE_MAPBOX_URL,
    MF_REMOTE_ADCREATOR_URL: import.meta.env.VITE_MF_REMOTE_ADCREATOR_URL,
    MF_REMOTE_BOOKING_URL: import.meta.env.VITE_MF_REMOTE_BOOKING_URL,
    MF_REMOTE_CAMPAIGN_URL: import.meta.env.VITE_MF_REMOTE_CAMPAIGN_URL,
    MF_REMOTE_CUSTOMER_URL: import.meta.env.VITE_MF_REMOTE_CUSTOMER_URL,
    MF_REMOTE_MAP_URL: import.meta.env.VITE_MF_REMOTE_MAP_URL,
    MF_REMOTE_ORGANIZATION_SETTINGS_URL: import.meta.env
      .VITE_MF_REMOTE_ORGANIZATION_SETTINGS_URL,
    MF_REMOTE_PERSONAL_SETTINGS_URL: import.meta.env
      .VITE_MF_REMOTE_PERSONAL_SETTINGS_URL,
    MF_REMOTE_PREVIEW_URL: import.meta.env.VITE_MF_REMOTE_PREVIEW_URL,
    MF_REMOTE_REPORTING_URL: import.meta.env.VITE_MF_REMOTE_REPORTING_URL,
    MF_REMOTE_TARGETING_URL: import.meta.env.VITE_MF_REMOTE_TARGETING_URL,
    MF_REMOTE_MEDIAPLANNING_URL: import.meta.env
      .VITE_MF_REMOTE_MEDIAPLANNING_URL,
    MF_REMOTE_ASSETS_LIBRARY_URL: import.meta.env
      .VITE_MF_REMOTE_ASSETS_LIBRARY_URL,
    MF_REMOTE_CLIENT_REPORTING_URL: import.meta.env
      .VITE_MF_REMOTE_CLIENT_REPORTING_URL,
    ORG_ID: import.meta.env.VITE_ORG_ID,
    PREVIEW_F11_HOST: import.meta.env.VITE_PREVIEW_F11_HOST,
    PREVIEW_NEUTRAL_HOST: import.meta.env.VITE_PREVIEW_NEUTRAL_HOST,
    STAGE_HOST_LIVE: import.meta.env.VITE_STAGE_HOST_LIVE,
    STAGE_HOST_PREVIEW: import.meta.env.VITE_STAGE_HOST_PREVIEW,
    SUPPORTED_LANGUAGES: import.meta.env.VITE_SUPPORTED_LANGUAGES,
    THEME_DEFAULT: import.meta.env.VITE_THEME_DEFAULT,
    THEME_URL: import.meta.env.VITE_THEME_URL,
    YOUTUBE_API_KEY: import.meta.env.VITE_YOUTUBE_API_KEY,
  };

  return {
    ...envConfig,
    ...loadAppConfigOverwrites(),
  };
};

const deriveServiceConfig = (baseAppConfig: BaseAppConfig): AppConfig => {
  if (!baseAppConfig.API_ROOT) {
    return {
      ...baseAppConfig,
      DOWNLOAD_URL: '',
      EXPORT_URL: '',
      FILE_URL: '',
      IMG_URL: '',
    };
  }
  return {
    ...baseAppConfig,
    DOWNLOAD_URL: String(
      new URL('/ad-manager/download/', baseAppConfig.API_ROOT),
    ),
    EXPORT_URL: String(new URL('/ad-manager/export/', baseAppConfig.API_ROOT)),
    FILE_URL: String(new URL('/ad-manager/file/', baseAppConfig.API_ROOT)),
    IMG_URL: String(new URL('/ad-manager/image/', baseAppConfig.API_ROOT)),
  };
};
export function loadAppConfig() {
  const result = appConfigDerivedSchema.safeParse(
    deriveServiceConfig(initAppConfig()),
  );
  if (!result.success) {
    throw new Error('AdmUI: appConfig validation failed', {
      cause: result.error,
    });
  }
  appConfig = Object.freeze(result.data);
  // if appConfing is not set on the window object, set it as
  // we are in an environment without domain service (review,local)
  if (!globalThis.appConfig) {
    globalThis.appConfig = appConfig;
  }
}

export function getAppConfig() {
  if (!appConfig) {
    throw new Error('getAppConfig() called before loadAppConfig()');
  }
  return appConfig;
}

export type { AppConfig };
